import type { BrowserModule, RuntimeBrowser, RuntimeContext } from "./types.js";
import type { Logger } from "../../data-model/ports.js";
import { loadBrowserModule } from "./loader.js";
import { safeCloseContext } from "./safe-close.js";

export interface BrowserPoolOptions {
  readonly moduleLoader?: () => Promise<BrowserModule>;
  readonly idleTimeoutMs?: number;
  readonly headed?: boolean;
  readonly executablePath?: string;
  readonly logger?: Logger;
}

export interface ContextHandle {
  readonly context: RuntimeContext;
  readonly release: () => Promise<void>;
}

export class BrowserPool {
  readonly #loader: () => Promise<BrowserModule>;
  readonly #idleMs: number;
  readonly #headed: boolean;
  readonly #execPath: string | undefined;
  readonly #logger: Logger | undefined;
  #module: BrowserModule | null = null;
  #browser: RuntimeBrowser | null = null;
  #active = new Set<RuntimeContext>();
  #idleTimer: NodeJS.Timeout | null = null;
  #launching: Promise<RuntimeBrowser> | null = null;
  #disposed: boolean = false;

  constructor(opts: BrowserPoolOptions = {}) {
    this.#loader = opts.moduleLoader ?? loadBrowserModule;
    this.#idleMs = opts.idleTimeoutMs ?? 60_000;
    this.#headed = opts.headed ?? false;
    this.#execPath = opts.executablePath;
    this.#logger = opts.logger;
  }

  async acquireContext(): Promise<ContextHandle> {
    if (this.#disposed) {
      throw new Error("BrowserPool: cannot acquire context after dispose()");
    }
    if (this.#idleTimer) {
      clearTimeout(this.#idleTimer);
      this.#idleTimer = null;
    }
    const browser = await this.#getBrowser();
    const context = await browser.newContext();
    if (this.#disposed) {
      await safeCloseContext(context);
      throw new Error("BrowserPool: disposed during context creation");
    }
    this.#active.add(context);
    return {
      context,
      release: async () => {
        if (!this.#active.has(context)) return;
        this.#active.delete(context);
        await safeCloseContext(context);
        this.#armIdleTimer();
      },
    };
  }

  async dispose(): Promise<void> {
    this.#disposed = true;
    if (this.#idleTimer) {
      clearTimeout(this.#idleTimer);
      this.#idleTimer = null;
    }
    if (this.#launching) {
      await this.#launching.catch(() => {});
      this.#launching = null;
    }
    for (const ctx of this.#active) {
      await safeCloseContext(ctx);
    }
    this.#active.clear();
    if (this.#browser) {
      try { await this.#browser.close(); } catch (err) { this.#logger?.debug({ err }, "browser close failed (non-fatal)"); }
      this.#browser = null;
    }
    this.#module = null;
    this.#launching = null;
    // dispose is one-way; #disposed = true makes later acquireContext() fail fast.
  }

  async #getBrowser(): Promise<RuntimeBrowser> {
    if (this.#browser?.isConnected()) return this.#browser;
    if (this.#launching) return this.#launching;
    const launching = (async () => {
      this.#module ??= await this.#loader();
      if (this.#disposed) throw new Error("BrowserPool disposed during launch");
      const launchOpts: { headless: boolean; executablePath?: string } = { headless: !this.#headed };
      if (this.#execPath) launchOpts.executablePath = this.#execPath;
      const b = await this.#module.chromium.launch(launchOpts);
      if (this.#disposed) {
        await b.close().catch(() => {});
        throw new Error("BrowserPool disposed during launch");
      }
      this.#browser = b;
      return b;
    })();
    this.#launching = launching;
    void launching
      .finally(() => {
        if (this.#launching === launching) this.#launching = null;
      })
      .catch(() => {});
    return launching;
  }

  #armIdleTimer(): void {
    if (this.#active.size > 0) return;
    if (this.#idleTimer) clearTimeout(this.#idleTimer);
    const timer = setTimeout(() => {
      this.#idleTimer = null;
      void this.#closeIdleBrowser();
    }, this.#idleMs);
    this.#idleTimer = timer;
    timer.unref();
  }

  async #closeIdleBrowser(): Promise<void> {
    if (this.#active.size > 0) return;
    if (!this.#browser) return;
    const b = this.#browser;
    this.#browser = null;
    try { await b.close(); } catch (err) { this.#logger?.debug({ err }, "idle browser close failed (non-fatal)"); }
  }
}
